/**
 * The exposure grid's filter bar.
 *
 * Deliberately dumb: it takes the parsed view and a `set` callback and owns no
 * local filter state. Everything it writes goes to the URL, so the control that
 * produced a number and the number itself can always be sent to someone together.
 * Row density lives in Appearance (global), not here.
 */

import { Button, Card, FilterChip, SearchInput, Segmented, Select } from '@w7/ui'
import { ExportCsvButton } from '@/app/ExportCsvButton'
import { EXPOSURE_SORTS, MARKET_TYPE_FILTERS, POLL_INTERVAL_MS, SPORT_FILTERS } from '@/app/queries'
import { count, money, pct } from '@/lib/format'
import type { Sport } from '@/data/types'
import type { ExposureSort } from '@/data/api'
import type { ExposureView, MarketTypeFilter } from './query'

/**
 * Round numbers only, and a picker rather than a box.
 *
 * A desk sets this filter with one click at a figure it already has in mind,
 * and a free-text box can be left in a half-typed state — "1000000" mid-keystroke
 * is "10,00,00" — that silently filters the whole book down to nothing.
 */
const WORSE_THAN_STEPS = [10_000, 50_000, 1_00_000, 5_00_000, 10_00_000]

export interface ExposureToolbarProps {
  view: ExposureView
  set: (next: Partial<ExposureView>) => void
  clearFilters: () => void
  isFiltered: boolean
  filterLabels: string[]
  /** The caller's sportsbook share, for the basis explainer. */
  share: number | undefined
  onRefresh: () => void
  isFetching: boolean
  onExport: () => void
  canExport: boolean
  counts: { events: number; markets: number; fancyLines: number }
}

export function ExposureToolbar({
  view,
  set,
  clearFilters,
  isFiltered,
  filterLabels,
  share,
  onRefresh,
  isFetching,
  onExport,
  canExport,
  counts,
}: ExposureToolbarProps) {
  const toggleSport = (sport: Sport) =>
    set({
      sports: view.sports.includes(sport)
        ? view.sports.filter((entry) => entry !== sport)
        : [...view.sports, sport],
    })

  // A `worse` value the picker does not offer still has to be honoured — it
  // arrived from a hand-edited link or the palette, and snapping it to "Any"
  // would silently show an operator more book than they asked for.
  const steps =
    view.worse !== null && !WORSE_THAN_STEPS.includes(view.worse)
      ? [view.worse, ...WORSE_THAN_STEPS].sort((a, b) => a - b)
      : WORSE_THAN_STEPS

  const pollSeconds = Math.round(POLL_INTERVAL_MS / 1000)

  return (
    <Card style={{ marginBottom: 'var(--w7-s5)' }}>
      <div className="w7-col">
        {/* ------------------------------------------------ lens + identity -- */}
        <div className="w7-row w7-row-wrap">
          <Segmented
            label="Exposure basis"
            value={view.basis}
            onChange={(basis) => set({ basis })}
            items={[
              { id: 'my', label: 'My share' },
              { id: 'total', label: 'Total book' },
            ]}
          />
          <SearchInput
            value={view.q}
            onChange={(q) => set({ q })}
            label="Search events, markets and runners"
            placeholder="Event, market or runner"
            width={280}
          />
          <span className="w7-spacer" />
          {/* A risk desk hits refresh. Refusing them the button does not stop
              them wanting it, it just makes the page feel stuck. */}
          <Button
            variant="ghost"
            icon="refresh"
            onClick={onRefresh}
            busy={isFetching}
            title={`The book polls on its own every ${pollSeconds} seconds; this asks now.`}
          >
            Refresh
          </Button>
          <ExportCsvButton onClick={onExport} disabled={!canExport} />
        </div>

        <p className="w7-card-sub">
          My share applies{' '}
          {share === undefined ? 'your sportsbook share' : `your ${pct(share, 0)} sportsbook share`}{' '}
          to every position. Total book is the whole downline&rsquo;s liability, before the split.
        </p>

        {/* ------------------------------------------------------- filters -- */}
        <div className="w7-row w7-row-wrap" style={{ gap: 6 }}>
          <FilterChip active={view.sports.length === 0} onClick={() => set({ sports: [] })}>
            All sports
          </FilterChip>
          {SPORT_FILTERS.map((sport) => (
            <FilterChip
              key={sport}
              active={view.sports.includes(sport)}
              onClick={() => toggleSport(sport)}
            >
              {sport}
            </FilterChip>
          ))}
          <FilterChip active={view.live} onClick={() => set({ live: !view.live })}>
            In play only
          </FilterChip>
        </div>

        {/* `.w7-selectwrap` is `width: 100%`, so three bare Selects in a flex
            row each claim a whole line. Each gets its own sized box instead —
            a filter set that costs three rows of vertical space on the screen
            whose point is density is a contradiction. */}
        <div className="w7-row w7-row-wrap w7-expo-filters">
          <Select
            aria-label="Market type"
            value={view.type}
            onChange={(event) => set({ type: event.target.value as MarketTypeFilter })}
            options={MARKET_TYPE_FILTERS.map((filter) => ({ value: filter.id, label: filter.label }))}
          />
          <Select
            aria-label="Show only positions worse than"
            value={view.worse === null ? '' : String(view.worse)}
            onChange={(event) => set({ worse: event.target.value ? Number(event.target.value) : null })}
            options={[
              { value: '', label: 'Any worst case' },
              ...steps.map((step) => ({
                value: String(step),
                label: `Worse than ${money(step, { round: true })}`,
              })),
            ]}
          />
          <Select
            aria-label="Sort events by"
            value={view.sort}
            onChange={(event) => set({ sort: event.target.value as ExposureSort })}
            options={EXPOSURE_SORTS.map((entry) => ({ value: entry.id, label: entry.label }))}
          />
          {/* Density lives in Appearance → Table density (global for every table). */}
          <span className="w7-spacer" />
          {isFiltered ? (
            <Button
              variant="ghost"
              icon="close"
              onClick={clearFilters}
              title="Clears the filters. Your My share / Total book choice and the sort order are kept."
            >
              Clear filters
            </Button>
          ) : null}
        </div>

        {/* The grid is not a DataTable, so it does not inherit DataTable's live
            region — and a filter set that changes the page silently is
            unusable without sight of it. This is that announcement channel. */}
        <p className="w7-card-sub" aria-live="polite">
          Showing {count(counts.events)} {counts.events === 1 ? 'event' : 'events'} ·{' '}
          {count(counts.markets)} {counts.markets === 1 ? 'market' : 'markets'} ·{' '}
          {count(counts.fancyLines)} fancy {counts.fancyLines === 1 ? 'line' : 'lines'}
          {filterLabels.length > 0 ? ` · filtered by ${filterLabels.join(', ')}` : ''}
        </p>
      </div>
    </Card>
  )
}
